//src/components/steps/Step3Confirm.jsx
import { useMemo } from "react";
import { MdCheckCircle } from "react-icons/md";
import { useSellProperty } from "../SellPropertyContext";
import { StepSection, SummarySection } from "../SellPropertyUI";
import {
  BUILDING_CONDITION_OPTIONS,
  BUILDING_ORIENTATION_OPTIONS,
  FURNISHING_STATUS_OPTIONS,
  LISTING_TYPE,
  RENT_DURATION,
} from "../sellProperty.constants";
import { formatRupiah } from "../sellProperty.utils";

const Step3Confirm = () => {
  const {
    form,
    propertyImages,
    selectedPropertyType,
    selectedCertificateType,
    featureGroups,
  } = useSellProperty();

  const selectedFeatureLabels = useMemo(() => {
    const flatFeatures = featureGroups.flatMap((group) =>
      (group.features || []).map((feature) => ({
        id: String(feature.id),
        name: feature.name,
        group: group.name,
      })),
    );

    return (form.feature_ids || [])
      .map((id) => flatFeatures.find((item) => item.id === String(id)))
      .filter(Boolean);
  }, [featureGroups, form.feature_ids]);

  const nearbyPlacesText = useMemo(() => {
    if (!form.nearby_places?.length) return "-";

    return form.nearby_places
      .filter((p) => p?.name)
      .map((p) => {
        const distance = p.distance_km ? ` (${p.distance_km} km)` : "";
        const category = p.category ? `${p.category}: ` : "";
        return `${category}${p.name}${distance}`;
      })
      .join(" • ");
  }, [form.nearby_places]);

  const furnishingLabel =
    FURNISHING_STATUS_OPTIONS.find((item) => item.value === form.furnishing_status)
      ?.label || "-";

  const conditionLabel =
    BUILDING_CONDITION_OPTIONS.find((item) => item.value === form.building_condition)
      ?.label || "-";

  const orientationLabel =
    BUILDING_ORIENTATION_OPTIONS.find((item) => item.value === form.building_orientation)
      ?.label || "-";

  const certificateLabel = selectedCertificateType?.name || "-";

  return (
    <StepSection
      icon={<MdCheckCircle size={20} color="var(--color-primary)" />}
      title="Ringkasan & Konfirmasi"
      desc="Periksa kembali informasi sebelum mengirimkan listing Anda."
    >
      <SummarySection
        title="Informasi Pemilik"
        rows={[
          { label: "Nama", value: form.ownerName },
          { label: "WhatsApp", value: form.ownerPhone },
          { label: "Email", value: form.ownerEmail || "-" },
        ]}
      />

      <SummarySection
        title="Detail Properti"
        rows={[
          {
            label: "Jenis Listing",
            value: LISTING_TYPE.find((t) => t.value === form.status)?.label || "-",
          },
          { label: "Tipe Properti", value: selectedPropertyType?.name || "-" },
          { label: "Sertifikat", value: certificateLabel },
          { label: "Judul", value: form.title || "-" },
          { label: "Provinsi", value: form.province || "-" },
          { label: "Kota / Kabupaten", value: form.city || "-" },
          { label: "Kecamatan", value: form.district || "-" },
          { label: "Desa", value: form.village || "-" },
          {
            label: "Alamat",
            value:
              [form.address, form.village, form.district, form.city, form.province]
                .filter(Boolean)
                .join(", ") || "-",
          },
          { label: "Lokasi / Area", value: form.location || "-" },
          { label: "Tahun Dibangun", value: form.construction_year || "-" },
          { label: "Tahun Renovasi", value: form.renovation_year || "-" },
          { label: "Garasi", value: form.garage_count || "-" },
          { label: "Carport", value: form.carport_count || "-" },
          { label: "Kondisi Perabotan", value: furnishingLabel },
          { label: "Daya Listrik", value: form.electric_power ? `${form.electric_power} Watt` : "-" },
          { label: "Hadap", value: orientationLabel },
          { label: "Kondisi Bangunan", value: conditionLabel },
          { label: "Luas Tanah", value: form.land_area ? `${form.land_area} m²` : "-" },
          { label: "Luas Bangunan", value: form.building_area ? `${form.building_area} m²` : "-" },
          { label: "Kamar Tidur", value: form.bedrooms || "-" },
          { label: "Kamar Mandi", value: form.bathrooms || "-" },
          { label: "Lantai", value: form.floors || "-" },
        ]}
      />

      <SummarySection
        title="Harga & Fitur"
        rows={[
          { label: "Harga", value: formatRupiah(form.price) },
          {
            label: "Durasi Sewa",
            value:
              form.status === "for_rent"
                ? RENT_DURATION.find((d) => d.value === form.sale_rent_duration)?.label
                : "-",
          },
          {
            label: "Fitur Dipilih",
            value: selectedFeatureLabels.length
              ? selectedFeatureLabels.map((item) => item.name).join(", ")
              : "-",
          },
          {
            label: "Lokasi Terdekat",
            value: nearbyPlacesText,
          },
          {
            label: "Foto Properti",
            value: propertyImages.length ? `${propertyImages.length} file` : "-",
          },
        ]}
      />

      <SummarySection
        title="Deskripsi"
        rows={[
          {
            label: "Isi",
            value: form.description ? "Sudah diisi" : "-",
          },
        ]}
      />

      <div className="sell-disclaimer">
        <span>💡</span>
        <span>
          Dengan mengirimkan formulir ini, Anda menyetujui bahwa informasi yang
          diberikan adalah benar dan Anda berwenang untuk menjual/menyewakan
          properti tersebut.
        </span>
      </div>
    </StepSection>
  );
};

export default Step3Confirm;
